import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Markdown from 'react-markdown';
import * as S from './NewsBox.style';
import { useStore } from '../../store/useStore';
import { trackEvent } from '../../services/analytics';

interface MarketAnalysis {
  summary: string;
  sentiment: string;
  confidence: number;
  timestamp: string;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m ago`;
}

function formatTimeUntil(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'soon';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

export const NewsBox = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasSeenFresh, setHasSeenFresh] = useState(false);
  const [, setTick] = useState(0);
  const dataFeed = useStore((state) => state.dataFeed);
  const aiAnalysis = useStore((state) => state.aiAnalysis);
  const analysisMeta = useStore((state) => state.analysisMeta);

  // Re-render every 30s to keep timing text current
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  const analysis = useMemo((): MarketAnalysis | null => {
    const { marketAnalysis } = dataFeed;
    if (!marketAnalysis) return null;

    try {
      return typeof marketAnalysis === 'string' ? JSON.parse(marketAnalysis) : marketAnalysis;
    } catch {
      return null;
    }
  }, [dataFeed]);

  const shouldPulse = !!analysisMeta?.lastRunAt && !hasSeenFresh;

  // Stop pulsing when drawer is opened while fresh
  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => {
      if (!prev) {
        trackEvent('desktop_ai_panel_open');
        setHasSeenFresh(true);
      }
      return !prev;
    });
  }, []);

  // Reset seen state when a new analysis arrives
  useEffect(() => {
    if (analysisMeta?.lastRunAt) {
      setHasSeenFresh(false);
    }
  }, [analysisMeta?.lastRunAt]);

  const previewSnippet = useMemo(() => {
    const source = analysis?.summary || aiAnalysis || 'awaiting context update';
    return source.replace(/\s+/g, ' ').trim();
  }, [analysis?.summary, aiAnalysis]);

  if (!analysis && !aiAnalysis) return null;

  const timingText = analysisMeta?.lastRunAt
    ? `updated ${formatTimeAgo(analysisMeta.lastRunAt)}${
        analysisMeta.nextRunAt ? ` · next in ${formatTimeUntil(analysisMeta.nextRunAt)}` : ''
      }`
    : null;

  return (
    <S.NewsBoxContainer $isExpanded={isExpanded}>
      <S.NewsBoxHeader $isExpanded={isExpanded} onClick={handleToggle}>
        <S.HeaderTop>
          <S.HeaderIdentity>
            {analysisMeta && <S.StatusDot $pulsing={shouldPulse} />}
            <S.HeaderText>
              <S.NewsBoxKicker>the.ai briefing</S.NewsBoxKicker>
              <S.NewsBoxTitle>Regime Context</S.NewsBoxTitle>
            </S.HeaderText>
          </S.HeaderIdentity>
          <S.HeaderRight>
            {timingText && <S.TimingMeta>{timingText}</S.TimingMeta>}
            <S.NewsBoxToggle $isExpanded={isExpanded}>
              {isExpanded ? 'close' : 'open'}
            </S.NewsBoxToggle>
          </S.HeaderRight>
        </S.HeaderTop>
        <S.PreviewSnippet $isExpanded={isExpanded}>{previewSnippet}</S.PreviewSnippet>
      </S.NewsBoxHeader>
      {isExpanded && (
        <S.NewsBoxContent>
          {analysis && (
            <>
              <S.AnalysisMeta>
                <S.Sentiment $sentiment={analysis.sentiment}>{analysis.sentiment}</S.Sentiment>
                <S.Confidence>conf: {analysis.confidence}/10</S.Confidence>
              </S.AnalysisMeta>
              <S.Summary>{analysis.summary}</S.Summary>
            </>
          )}
          {aiAnalysis && (
            <S.MarkdownContent>
              <Markdown>{aiAnalysis}</Markdown>
            </S.MarkdownContent>
          )}
        </S.NewsBoxContent>
      )}
    </S.NewsBoxContainer>
  );
};
